import React from "react";
import { Quote } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Motion";
import { motion } from "framer-motion";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { TESTIMONIALS } from "@/lib/site-data";

export default function TestimonialsPage() {
  return (
    <div data-testid="page-testimonials">
      <Seo
        title="Client & Career Stories | Cutting Edge Technologies Consulting"
        description="Real outcomes from CTOs, hiring teams, and career-changers. Explore success stories from Cutting Edge Technologies clients in Houston TX, Washington DC and beyond."
        path="/testimonials"
      />
      <section className="relative bg-slate-950 text-white pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-60" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal><div className="eyebrow text-emerald-400">Stories</div></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              Operators and learners. Real outcomes.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate-300 leading-relaxed">
              A small selection of the teams and individuals we've partnered with — from CIOs to career-changers.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {TESTIMONIALS.map((t, i) => {
              // varied spans for visual rhythm
              const span =
                i === 0 ? "lg:col-span-7" :
                i === 1 ? "lg:col-span-5" :
                i === 2 ? "lg:col-span-5" :
                i === 3 ? "lg:col-span-7" :
                i === 4 ? "lg:col-span-6" :
                i === 5 ? "lg:col-span-6" :
                i === 6 ? "lg:col-span-7" :
                "lg:col-span-5";
              const dark = i === 0 || i === 3 || i === 5 || i === 6;
              return (
                <motion.figure
                  key={i}
                  variants={staggerItem}
                  className={`${span} md:col-span-1 p-10 ${
                    dark
                      ? "bg-slate-950 text-white border border-slate-900"
                      : "bg-slate-50 text-slate-900 border border-slate-200"
                  }`}
                  data-testid={`testimonial-${i}`}
                >
                  <Quote className={`w-8 h-8 ${dark ? "text-emerald-400" : "text-emerald-600"}`} strokeWidth={1.5} />
                  <blockquote className={`mt-6 font-display text-2xl md:text-3xl leading-tight tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className={`mt-10 pt-6 border-t ${dark ? "border-slate-800" : "border-slate-200"}`}>
                    <div className={`font-medium ${dark ? "text-white" : "text-slate-900"}`}>{t.name}</div>
                    <div className={`text-sm mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>
                      {t.role} · {t.company}
                    </div>
                  </figcaption>
                </motion.figure>
              );
            })}
          </Stagger>
        </div>
      </section>

      <CtaBanner
        title="Join the list of operators we partner with."
        body="We'd love to hear what you're building and how we can help."
      />
    </div>
  );
}
